import React from 'react';
import { useApi } from '@/api/ApiContext';
import {
    clearAuthSession,
    hasLiveAccessToken,
    isAuthenticated,
} from '../utils';

export type SessionGuardState = 'signedIn' | 'restoring' | 'signedOut';

const initialState = (): SessionGuardState => {
    if (!isAuthenticated()) {
        return 'signedOut';
    }
    return hasLiveAccessToken() ? 'signedIn' : 'restoring';
};

/**
 * Decides whether the stored seller session lets the user in. A refresh token
 * is what counts: on a reload after the access token lapsed (15 minutes), the
 * session is refreshed **before** any redirect. Only a server rejection of the
 * refresh token signs the user out; temporary trouble keeps them in, and the
 * interceptor refreshes again on the next call.
 */
export const useSessionGuard = (): SessionGuardState => {
    const { apiClient } = useApi();
    const [state, setState] = React.useState(initialState);

    React.useEffect(() => {
        if (state !== 'restoring') {
            return;
        }
        let cancelled = false;
        void apiClient.auth.refreshSession().then((outcome) => {
            if (cancelled) {
                return;
            }
            if (outcome.status === 'rejected') {
                clearAuthSession(outcome.reason);
                setState('signedOut');
            } else {
                setState('signedIn');
            }
        });
        return () => {
            cancelled = true;
        };
    }, [apiClient, state]);

    // A session cleared elsewhere (lock, sign-out) wins over the cached state.
    return isAuthenticated() ? state : 'signedOut';
};
